<template>
  <div class="about-container">
    <svg class="spray-filter-defs" aria-hidden="true" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="epitaph-spray-roughen" x="-50%" y="-50%" width="200%" height="200%">
          <feTurbulence type="fractalNoise" baseFrequency="0.012 0.02" numOctaves="2" seed="7" result="noise"/>
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="5" xChannelSelector="R" yChannelSelector="G"/>
        </filter>
      </defs>
    </svg>
    <PageHeader>
      {{ $t('about.title') }}
      <template #subtitle>{{ $t('about.subtitle') }}</template>
    </PageHeader>
    
    <div class="papers-stack">
      <div 
        v-for="(ver, index) in about.versions" 
        :key="ver.id"
        class="paper-card"
        :class="{ 'paper-card--spray': ver.display === 'spray' }"
        :style="getCardStyle(index)"
        @click="handleCardClick(index)"
      >
        <div class="paper-meta">
          <span class="version-date">{{ ver.date }}</span>
          <span class="version-author">{{ ver.author[currentLocale] }}</span>
        </div>
        <h2 class="version-title" v-if="ver.title">{{ ver.title[currentLocale] }}</h2>
        <div class="about-content" v-if="ver.display === 'spray'">
          <div class="epitaph-spray">
            <div class="flame-stage">
              <canvas ref="flameCanvas" class="flame-canvas"></canvas>
              <p class="spray-line spray-french">{{ sprayParts(ver, currentLocale).french }}</p>
            </div>
            <p class="spray-line spray-translation">{{ sprayParts(ver, currentLocale).translation }}</p>
          </div>
        </div>
        <div class="about-content" v-else>
          <div class="epitaph-text">{{ ver.contentKey[currentLocale] }}</div>
        </div>
        <div class="flip-hint" v-if="index === currentIndex">
          <span class="version-counter">{{ currentLocale === 'zh' ? `第 ${currentIndex + 1} / ${about.versions.length} 版` : `Version ${currentIndex + 1} / ${about.versions.length}` }}</span>
          <span>{{ currentLocale === 'zh' ? '点击纸张翻阅下一版' : 'Click to flip to next version' }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path></svg>
        </div>
      </div>
    </div>
    
    <div class="signature" v-if="currentVersion?.signature">
      <p>{{ currentVersion.signature[currentLocale] }}</p>
    </div>

    <div class="traces-gallery" v-if="about.personalWorks && about.personalWorks.length">
      <h2>{{ $t('about.sectionTitle') }}</h2>
      <div class="decorative-line"></div>
      
      <div class="works-list">
        <div class="work-item" v-for="(work, index) in about.personalWorks" :key="index">
          <div class="work-header">
            <h3>{{ work.titleKey[currentLocale] }}</h3>
            <span v-if="work.status" class="work-annotation">{{ $t(`about.status.${work.status}`) }}</span>
          </div>
          <p>{{ work.descriptionKey[currentLocale] }}</p>
          <a v-if="work.link" :href="work.link" target="_blank" rel="noopener noreferrer" class="work-link">
            {{ $t('common.actions.viewProject') }}
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import config from '../config'
import PageHeader from '@/components/PageHeader.vue'
import { useI18n } from 'vue-i18n'

export default {
  name: 'About',
  components: { PageHeader },
  setup() {
    const { t, locale } = useI18n()
    return { t, locale }
  },
  data() {
    return {
      about: config.about,
      currentIndex: 0,
      // 火焰粒子引擎（仅喷漆墓志铭卡片为前景时运行）
      flameState: {
        rafId: null,
        ctx: null,
        canvas: null,
        dpr: 1,
        w: 0,
        h: 0,
        flames: [],
        embers: [],
        ash: [],
        textLines: [],
        frames: 0,
        lastSpawn: 0,
        running: false,
        reduceMotion: false
      }
    }
  },
  computed: {
    currentLocale() {
      return this.$i18n.locale
    },
    currentVersion() {
      return this.about.versions?.[this.currentIndex] ?? null
    },
    // 当前是否是喷漆卡片位于前景（火焰仅在此时运行，省性能）
    sprayActive() {
      const v = this.about.versions?.[this.currentIndex]
      return v?.display === 'spray'
    }
  },
  watch: {
    sprayActive(active) {
      if (active) this.startFlames();
      else this.stopFlames();
    }
  },
  mounted() {
    this.flameState.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('resize', this.onResize);
    if (this.sprayActive) this.$nextTick(this.startFlames);
  },
  beforeUnmount() {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('resize', this.onResize);
    this.stopFlames();
  },
  methods: {
    sprayParts(ver, locale) {
      // spray 变体按空行把「法语句」与「译文」拆成两块，分别排布
      const raw = ver.contentKey?.[locale] || ''
      const [french = '', ...rest] = raw.split(/\n\s*\n/)
      return { french: french.trim(), translation: rest.join('\n\n').trim() }
    },
    getCardStyle(index) {
      if (!this.about.versions) return {};
      const total = this.about.versions.length;
      let diff = index - this.currentIndex;
      if (diff < 0) diff += total;
      
      if (diff === 0) {
        return {
          transform: 'translateY(0) rotate(0deg) scale(1)',
          zIndex: 10,
          opacity: 1,
          pointerEvents: 'auto'
        }
      }
      
      const rotations = [2, -2, 3, -3, 1];
      const rot = rotations[index % rotations.length];
      
      return {
        transform: `translateY(${diff * 12}px) translateX(${diff * 6}px) rotate(${rot}deg) scale(${1 - diff * 0.02})`,
        zIndex: 10 - diff,
        opacity: 1 - (diff * 0.15),
        pointerEvents: 'auto',
        cursor: 'pointer'
      }
    },
    handleCardClick(index) {
      if (!this.about.versions) return;
      if (this.currentIndex === index) {
        this.currentIndex = (this.currentIndex + 1) % this.about.versions.length;
      } else {
        this.currentIndex = index;
      }
    },
    // —— 火焰引擎 ——
    onVisibilityChange() {
      // 切回前台：来一波灰烬，像余烬被风吹散复燃
      if (!document.hidden && this.sprayActive && !this.flameState.reduceMotion) {
        this.burstAsh(28);
      }
      if (document.hidden) {
        this.stopFlames();
      } else if (this.sprayActive) {
        this.startFlames();
      }
    },
    startFlames() {
      const s = this.flameState;
      if (s.running || s.reduceMotion) return;
      // v-for 内的 ref 是数组，取第一个（喷漆卡片唯一一个 canvas）
      const canvas = (this.$refs.flameCanvas || [])[0];
      if (!canvas) return;
      s.canvas = canvas;
      s.ctx = canvas.getContext('2d');
      s.running = true;
      this.resizeFlameCanvas();
      s.lastSpawn = 0;
      // 字体加载完成后再测一次字形位置（首测可能落在 fallback 字体上）
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => { if (s.running) this.measureTextLines(); });
      }
      const loop = (t) => {
        if (!s.running) return;
        this.tickFlames(t);
        s.rafId = requestAnimationFrame(loop);
      };
      s.rafId = requestAnimationFrame(loop);
    },
    stopFlames() {
      const s = this.flameState;
      s.running = false;
      if (s.rafId) cancelAnimationFrame(s.rafId);
      s.rafId = null;
      // 清场
      if (s.ctx) s.ctx.clearRect(0, 0, s.w, s.h);
      s.flames = [];
      s.embers = [];
      s.ash = [];
    },
    resizeFlameCanvas() {
      const s = this.flameState;
      const canvas = s.canvas;
      if (!canvas) return;
      const host = canvas.parentElement;
      const rect = host.getBoundingClientRect();
      s.dpr = Math.min(window.devicePixelRatio || 1, 2);
      s.w = Math.max(1, rect.width);
      s.h = Math.max(1, rect.height);
      canvas.width = s.w * s.dpr;
      canvas.height = s.h * s.dpr;
      canvas.style.width = s.w + 'px';
      canvas.style.height = s.h + 'px';
      s.ctx.setTransform(s.dpr, 0, 0, s.dpr, 0, 0);
      this.measureTextLines();
    },
    onResize() {
      if (this.flameState.running) this.resizeFlameCanvas();
    },
    // 量取喷漆法文逐行的渲染矩形（相对画布），让火舌根部贴着字走，而非整片矩形随机
    measureTextLines() {
      const s = this.flameState;
      const canvas = s.canvas;
      if (!canvas) return;
      const stageRect = canvas.getBoundingClientRect();
      const frenchEl = canvas.parentElement && canvas.parentElement.querySelector('.spray-french');
      if (!frenchEl) { s.textLines = []; return; }
      const range = document.createRange();
      range.selectNodeContents(frenchEl);
      const rects = range.getClientRects();
      const lines = [];
      for (let i = 0; i < rects.length; i++) {
        const r = rects[i];
        if (r.width < 2 || r.height < 2) continue;
        lines.push({
          left: r.left - stageRect.left,
          right: r.right - stageRect.left,
          top: r.top - stageRect.top,
          bottom: r.bottom - stageRect.top
        });
      }
      s.textLines = lines;
    },
    spawnFlame() {
      const s = this.flameState;
      // 油画式火舌：以金橙红烈焰为主，偶尔混入品红/青/蓝白冷焰
      const hueRoll = Math.random();
      let hueOuter, hueMid, hueCore;
      if (hueRoll < 0.6) {            // 经典金橙红烈焰
        hueOuter = 8 + Math.random() * 10;
        hueMid = 28 + Math.random() * 10;
        hueCore = 48 + Math.random() * 10;
      } else if (hueRoll < 0.82) {    // 品红→紫
        hueOuter = 330 + Math.random() * 20;
        hueMid = 350 + Math.random() * 20;
        hueCore = 40 + Math.random() * 15;
      } else if (hueRoll < 0.93) {    // 青
        hueOuter = 190 + Math.random() * 20;
        hueMid = 175 + Math.random() * 20;
        hueCore = 55 + Math.random() * 15;
      } else {                        // 蓝白核心冷焰
        hueOuter = 210 + Math.random() * 20;
        hueMid = 30 + Math.random() * 15;
        hueCore = 55 + Math.random() * 10;
      }
      // 根部贴字：在量到的文字行里挑一点作火舌根，让火焰跟着字走
      let x, baseY;
      const lines = s.textLines;
      if (lines && lines.length) {
        const ln = lines[Math.floor(Math.random() * lines.length)];
        x = ln.left + Math.random() * (ln.right - ln.left);
        // 根部落在线条下半部，火舌由此向上舔
        baseY = ln.bottom - Math.random() * (ln.bottom - ln.top) * 0.5;
      } else {
        x = s.w * (0.1 + Math.random() * 0.8);
        baseY = s.h * 0.6;
      }
      // 高度上限 = 根到画布顶的距离，保证火舌绝不冲出画布顶（修「超框」）
      const maxReach = Math.max(18, baseY - 3);
      const wantHeight = s.h * (0.14 + Math.random() * 0.26);
      s.flames.push({
        x,
        baseY,
        height: Math.min(wantHeight, maxReach),
        width: s.w * (0.025 + Math.random() * 0.04) + 6,
        life: 1,
        decay: 0.006 + Math.random() * 0.006,        // 烧慢点：寿命更长
        hueOuter, hueMid, hueCore,
        wob: Math.random() * Math.PI * 2,
        wobSpeed: 0.035 + Math.random() * 0.03,      // 摆动变慢
        flick: Math.random() * Math.PI * 2
      });
    },
    // 单条火舌：三层 bezier 舌形（外红→中橙→核心黄白），source-over 叠色如油画颜料
    drawTongue(p) {
      const s = this.flameState;
      const ctx = s.ctx;
      const life = p.life;
      const flick = 0.92 + Math.abs(Math.sin(p.flick)) * 0.08;
      const reach = p.height * (0.5 + 0.5 * life) * flick;
      const w = p.width * (0.7 + 0.3 * life);
      const baseY = p.baseY;
      const wob = Math.sin(p.wob) * w * 0.4;

      const layers = [
        { scale: 1.0,  hue: p.hueOuter, light: 44, alpha: 0.5  * life },
        { scale: 0.62, hue: p.hueMid,   light: 56, alpha: 0.65 * life },
        { scale: 0.32, hue: p.hueCore,  light: 82, alpha: 0.85 * life }
      ];
      for (const L of layers) {
        const lw = w * L.scale;
        const lr = reach * (0.62 + 0.38 * L.scale);
        const lTipY = baseY - lr;
        const lTipX = p.x + wob * (0.5 + 0.5 * L.scale);
        ctx.beginPath();
        ctx.moveTo(p.x - lw * 0.5, baseY);
        ctx.bezierCurveTo(
          p.x - lw * 0.8, baseY - lr * 0.32,
          lTipX - lw * 0.12, lTipY + lr * 0.2,
          lTipX, lTipY
        );
        ctx.bezierCurveTo(
          lTipX + lw * 0.12, lTipY + lr * 0.2,
          p.x + lw * 0.8, baseY - lr * 0.32,
          p.x + lw * 0.5, baseY
        );
        ctx.closePath();
        const grd = ctx.createLinearGradient(0, baseY, 0, lTipY);
        const a = L.alpha;
        grd.addColorStop(0,   `hsla(${L.hue}, 100%, ${Math.min(96, L.light + 14)}%, ${a})`);
        grd.addColorStop(0.5, `hsla(${L.hue}, 100%, ${L.light}%, ${a})`);
        grd.addColorStop(1,   `hsla(${L.hue - 6}, 100%, ${Math.max(32, L.light - 16)}%, ${a * 0.1})`);
        ctx.fillStyle = grd;
        ctx.fill();
      }
    },
    // 从某条火舌根部溅出火星：上下火舌都喷，不再只聚底部
    spawnEmberAt(x, y, hue) {
      const s = this.flameState;
      const hueRoll = Math.random();
      if (hue === undefined) {
        hue = hueRoll < 0.7 ? 38 + Math.random() * 20 : (hueRoll < 0.88 ? 320 + Math.random() * 30 : 190 + Math.random() * 30);
      }
      s.embers.push({
        x: x + (Math.random() - 0.5) * 4,
        y: y + (Math.random() - 0.5) * 4,
        vx: (Math.random() - 0.5) * 0.8,
        vy: -(0.6 + Math.random() * 1.4),
        life: 1,
        decay: 0.004 + Math.random() * 0.006,
        size: 1 + Math.random() * 1.8,
        hue,
        flick: Math.random() * Math.PI * 2
      });
    },
    // 从某条火舌根部飘落一点灰烬：让灰烬也跟着字走，而非只在底部
    spawnAshAt(x, y) {
      const s = this.flameState;
      s.ash.push({
        x,
        y,
        vx: (Math.random() - 0.5) * 0.8,
        vy: 0.2 + Math.random() * 0.4,
        life: 1,
        decay: 0.003 + Math.random() * 0.004,
        size: 1 + Math.random() * 2.2,
        rot: Math.random() * Math.PI,
        vrot: (Math.random() - 0.5) * 0.05
      });
    },
    burstAsh(n) {
      const s = this.flameState;
      if (!s.canvas) this.resizeFlameCanvas();
      for (let i = 0; i < n; i++) {
        s.ash.push({
          x: s.w * (0.1 + Math.random() * 0.8),
          y: s.h * (0.3 + Math.random() * 0.4),
          vx: (Math.random() - 0.5) * 1.2,
          vy: -(0.2 + Math.random() * 0.6),
          life: 1,
          decay: 0.003 + Math.random() * 0.004,
          size: 1 + Math.random() * 2.4,
          rot: Math.random() * Math.PI,
          vrot: (Math.random() - 0.5) * 0.05
        });
      }
      // 灰烬需要持续渲染一小段时间；若未运行则起一个短循环
      if (!s.running) this.runAshTail();
    },
    runAshTail() {
      const s = this.flameState;
      if (s.ash.length === 0) return;
      let start = 0;
      const tail = (t) => {
        if (!start) start = t;
        this.tickAsh(t);
        if (s.ash.length > 0 && t - start < 4000) {
          requestAnimationFrame(tail);
        } else if (!s.running) {
          if (s.ctx) s.ctx.clearRect(0, 0, s.w, s.h);
        }
      };
      requestAnimationFrame(tail);
    },
    tickFlames(t) {
      const s = this.flameState;
      if (!s.ctx) return;
      if (!s.lastSpawn) s.lastSpawn = t;
      s.lastSpawn = t;
      s.frames++;
      // 每隔一阵重测字形位置，吸收翻页过渡 / 布局变化
      if (s.frames % 30 === 0) this.measureTextLines();
      // 生成：每帧 1 条火舌（贴字，铺满但不过载）
      const spawnCount = 1;
      for (let i = 0; i < spawnCount; i++) this.spawnFlame();

      // 火舌溅火星 / 飘灰烬：每条火舌都有机会，上下都喷（修「上面没灰烬」）
      if (s.flames.length) {
        const emit = Math.min(s.flames.length, 2);
        for (let i = 0; i < emit; i++) {
          if (Math.random() < 0.5) {
            const f = s.flames[(Math.random() * s.flames.length) | 0];
            this.spawnEmberAt(f.x, f.baseY, f.hueMid);
          }
        }
        if (Math.random() < 0.08) {
          const f = s.flames[(Math.random() * s.flames.length) | 0];
          this.spawnAshAt(f.x, f.baseY);
        }
      }

      s.ctx.clearRect(0, 0, s.w, s.h);

      // 火舌：source-over 叠色，扁平油画感
      s.ctx.globalCompositeOperation = 'source-over';
      for (let i = s.flames.length - 1; i >= 0; i--) {
        const p = s.flames[i];
        p.wob += p.wobSpeed;
        p.flick += 0.07;   // 闪烁变慢
        p.life -= p.decay;
        if (p.life <= 0) { s.flames.splice(i, 1); continue; }
        this.drawTongue(p);
      }

      // 余烬：保留 lighter 发光
      s.ctx.globalCompositeOperation = 'lighter';
      for (let i = s.embers.length - 1; i >= 0; i--) {
        const e = s.embers[i];
        e.flick += 0.2;
        e.x += e.vx + Math.sin(e.flick) * 0.3;
        e.y += e.vy;
        e.life -= e.decay;
        if (e.life <= 0 || e.y < -10) { s.embers.splice(i, 1); continue; }
        const flick = 0.6 + Math.abs(Math.sin(e.flick)) * 0.4;
        s.ctx.fillStyle = `hsla(${e.hue}, 100%, 75%, ${e.life * flick})`;
        s.ctx.beginPath();
        s.ctx.arc(e.x, e.y, e.size, 0, Math.PI * 2);
        s.ctx.fill();
      }

      s.ctx.globalCompositeOperation = 'source-over';
      this.tickAsh(t);
    },
    tickAsh() {
      const s = this.flameState;
      if (!s.ctx || s.ash.length === 0) return;
      for (let i = s.ash.length - 1; i >= 0; i--) {
        const a = s.ash[i];
        a.x += a.vx;
        a.y += a.vy;
        a.vy += 0.006; // 灰烬会缓缓下落
        a.vx *= 0.99;
        a.rot += a.vrot;
        a.life -= a.decay;
        if (a.life <= 0) { s.ash.splice(i, 1); continue; }
        s.ctx.save();
        s.ctx.translate(a.x, a.y);
        s.ctx.rotate(a.rot);
        s.ctx.fillStyle = `rgba(180, 170, 160, ${a.life * 0.7})`;
        s.ctx.fillRect(-a.size, -a.size * 0.5, a.size * 2, a.size);
        s.ctx.restore();
      }
    }
  }
}
</script>

<style scoped lang="scss">
.about-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1rem;
}

.papers-stack {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  margin-bottom: 4rem;
  perspective: 1000px;
}

.paper-card {
  grid-column: 1;
  grid-row: 1;
  background-color: var(--card-bg);
  border-radius: 4px;
  padding: 2.5rem;
  box-shadow: 0 5px 25px rgba(0,0,0,0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  transition: all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
  transform-origin: center center;
  
  &::before {
    content: '"';
    position: absolute;
    top: 2rem;
    left: 2rem;
    font-family: var(--font-display);
    font-size: 5rem;
    color: rgba(0, 0, 0, 0.05);
    line-height: 0;
  }
}

.paper-meta {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  font-family: var(--font-body);
  font-style: italic;
  color: var(--accent-color);
  font-size: 0.95rem;
  border-bottom: 1px dashed rgba(0,0,0,0.1);
  padding-bottom: 0.5rem;
  position: relative;
  z-index: 1;
}

.version-title {
  font-family: var(--font-display);
  font-size: 1.6rem;
  color: var(--primary-color);
  margin-bottom: 1.5rem;
  text-align: center;
  position: relative;
  z-index: 1;
}

.flip-hint {
  text-align: right;
  margin-top: 2rem;
  font-family: var(--font-body);
  font-style: italic;
  color: var(--accent-color);
  font-size: 0.9rem;
  opacity: 0.7;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  position: relative;
  z-index: 1;
  
  svg {
    animation: bounce-x 2s infinite;
  }
  
  .version-counter {
    margin-right: auto;
    font-style: normal;
    color: var(--secondary-color);
    font-size: 0.8rem;
    letter-spacing: 0.05em;
  }
}

@keyframes bounce-x {
  0%, 100% { transform: translateX(0); }
  50% { transform: translateX(5px); }
}

.about-content {
  font-family: var(--font-body);
  font-size: 1.1rem;
  line-height: 1.9;
  color: var(--secondary-color);
  position: relative;
  z-index: 1;
}

.epitaph-text {
  white-space: pre-line;
  font-family: var(--font-body);
  font-size: 1.1rem;
  line-height: 1.9;
  color: var(--secondary-color);
}

/* —— 喷漆墓志铭变体（Disco Elysico 式地面涂鸦）—— */
.spray-filter-defs {
  position: absolute;
  width: 0;
  height: 0;
  pointer-events: none;
}

.paper-card--spray {
  background-color: #1c1b18;
  background-image:
    radial-gradient(circle at 28% 18%, rgba(255, 255, 255, 0.04), transparent 55%),
    radial-gradient(circle at 72% 82%, rgba(0, 0, 0, 0.45), transparent 70%);
  border-color: rgba(0, 0, 0, 0.5);
  color: var(--light-text);

  &::before {
    content: none;
  }

  .paper-meta {
    color: rgba(var(--accent-color-rgb), 0.72);
    border-bottom-color: rgba(var(--accent-color-rgb), 0.18);
  }

  .version-title {
    color: rgba(245, 245, 245, 0.82);
  }

  .flip-hint .version-counter {
    color: rgba(245, 245, 245, 0.5);
  }
}

.epitaph-spray {
  text-align: center;
  padding: 1.2rem 0.5rem 0.5rem;
  position: relative;
  z-index: 1;
}

.flame-stage {
  position: relative;
  /* 顶部留足空间让火舌向上舔，不至于冲出卡片框 */
  padding-top: clamp(3.5rem, 12vw, 7rem);
}

.flame-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.spray-line {
  margin: 0;
  position: relative;
  z-index: 1;
}

.spray-french {
  font-family: var(--font-display);
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: clamp(2rem, 7vw, 4.5rem);
  line-height: 1.12;
  text-wrap: balance;
  color: #ffe9b8;
  filter: url(#epitaph-spray-roughen);
  text-shadow:
    0 0 2px rgba(255, 245, 215, 0.95),
    0 0 9px rgba(255, 185, 90, 0.7),
    0 0 22px rgba(255, 130, 45, 0.5),
    0 0 44px rgba(255, 90, 30, 0.32),
    0 0 80px rgba(200, 110, 50, 0.22);
  padding: 0.2em 0.1em;
  animation: epitaph-shimmer 4.5s ease-in-out infinite;
}

.spray-translation {
  margin-top: 1.6rem;
  font-family: var(--font-body);
  font-weight: 400;
  font-size: clamp(1.05rem, 3vw, 1.7rem);
  letter-spacing: 0.14em;
  color: rgba(245, 245, 245, 0.68);
  text-shadow: 0 0 8px rgba(0, 0, 0, 0.5);
}

@keyframes epitaph-shimmer {
  0%, 100% {
    text-shadow:
      0 0 2px rgba(255, 245, 215, 0.9),
      0 0 9px rgba(255, 185, 90, 0.6),
      0 0 22px rgba(255, 130, 45, 0.45),
      0 0 44px rgba(255, 90, 30, 0.28),
      0 0 80px rgba(200, 110, 50, 0.18);
  }
  50% {
    text-shadow:
      0 0 3px rgba(255, 252, 230, 1),
      0 0 16px rgba(255, 205, 110, 0.85),
      0 0 34px rgba(255, 150, 55, 0.6),
      0 0 66px rgba(255, 105, 35, 0.42),
      0 0 110px rgba(220, 130, 60, 0.26);
  }
}

@media (prefers-reduced-motion: reduce) {
  .spray-french {
    animation: none;
  }
}

.signature {
  text-align: right;
  margin-top: 4rem;
  margin-bottom: 4rem;
  
  p {
    font-family: var(--font-display);
    font-style: italic;
    font-size: 1.5rem;
    color: var(--accent-color);
  }
}

.traces-gallery {
  margin-top: 4rem;
  
  h2 {
    font-family: var(--font-display);
    font-size: 2.2rem;
    color: var(--primary-color);
    text-align: center;
    margin-bottom: 1rem;
  }
  
  .decorative-line {
    width: 80px;
    margin: 0 auto 3rem;
  }
  
  .works-list {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }
  
  .work-item {
    background-color: var(--card-bg);
    border-radius: 4px;
    padding: 2rem;
    box-shadow: 0 3px 10px var(--shadow-color);
    border: 1px solid rgba(0, 0, 0, 0.05);
    
    .work-header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 1rem;
      margin-bottom: 1rem;
      
      h3 {
        font-family: var(--font-display);
        font-size: 1.5rem;
        color: var(--primary-color);
        margin: 0;
      }
      
      .work-annotation {
        flex-shrink: 0;
        font-family: var(--font-body);
        font-size: 0.8rem;
        font-style: italic;
        color: var(--secondary-color);
        opacity: 0.75;
        letter-spacing: 0.03em;

        &::before {
          content: '·';
          margin-right: 0.35em;
          opacity: 0.65;
        }
      }
    }
    
    p {
      font-family: var(--font-body);
      font-size: 1.05rem;
      color: var(--secondary-color);
      line-height: 1.7;
      margin-bottom: 1rem;
    }
    
    .work-link {
      display: inline-block;
      color: var(--accent-color);
      text-decoration: none;
      font-family: var(--font-body);
      font-weight: bold;
      transition: color 0.3s ease;
      
      &:hover {
        color: var(--primary-color);
      }
    }
  }
}

@media (max-width: 768px) {
  .about-container {
    padding: 1.5rem 1rem;
  }
  
  .paper-card {
    padding: 1.5rem;
  }
}

@media (max-width: 480px) {
  .paper-card {
    padding: 1.2rem;
  }
}
</style>
